import { createStyles, withStyles, type WithStyles } from "@material-ui/core";
import type { Theme } from "@material-ui/core/styles";
import { CustomTable } from "features/shared/components";
import React, { type CSSProperties } from "react";
import arrayUtils from "shared/arrayUtils";
import type { EnrichedAnimalCategory } from "shared/types";
import type {
  CategoryFieldsVisibility,
  CategoryFilterFormState,
} from "../category.types";
import { useCategoryListTable } from "../hooks/useCategoryListTable";
import { filterCategoryPredicate } from "../utils/category.utils";

type CategoryListTableProps = {
  categories: EnrichedAnimalCategory[];
  fieldsVisibility: CategoryFieldsVisibility;
  filterForm: CategoryFilterFormState;
  isCategoryFormPanelOpen: boolean;
  onEditClick: (row: EnrichedAnimalCategory) => void;
  onToggleCategoryStatus: (
    row: EnrichedAnimalCategory,
    isCurrentDeleted: boolean
  ) => void;
} & WithStyles<typeof styles>;

function CategoryListTable({
  categories,
  classes,
  fieldsVisibility,
  filterForm,
  isCategoryFormPanelOpen,
  onEditClick,
  onToggleCategoryStatus,
}: CategoryListTableProps) {
  const { columns } = useCategoryListTable({
    classNames: { yesChip: classes.yesChip, noChip: classes.noChip },
    fieldsVisibility,
    isCategoryFormPanelOpen,
    onEditClick,
    onToggleCategoryStatus,
  });

  const filteredRows = arrayUtils.filterItems({
    items: categories,
    predicate: (item) => filterCategoryPredicate(item, filterForm),
  });

  return (
    <CustomTable
      columns={columns}
      getRowId={(row) => row.id.toString()}
      id="category-list-table"
      rows={filteredRows}
    />
  );
}

const styles = (theme: Theme) => {
  const defaultChip = {
    fontWeight: 600,
    width: 80,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 100,
  } satisfies CSSProperties;

  return createStyles({
    yesChip: {
      ...defaultChip,
      backgroundColor: "#dff1df",
      color: theme.palette.secondary.main,
    },
    noChip: {
      ...defaultChip,
      backgroundColor: "#f8807833",
      color: theme.palette.error.main,
    },
  });
};

export default withStyles(styles)(CategoryListTable);
